import { Button, ButtonVariant, ClearButtonVisible, Panel, PrintableEvent, TableRow, TableRowDisplayEvent, TableRowMode, Textbox } from "@mcleod/components";
import { AutogenLayoutCustomFuelIndex } from "./autogen/AutogenLayoutCustomFuelIndex";
import { HorizontalAlignment, LogManager } from "@mcleod/core";
import { ImageName } from "@mcleod/images";
import { CommonDialogs } from "@mcleod/common";

const log = LogManager.getLogger("lme.ar.fuelsurcharge.CustomFuelIndex");
const MAX_REGION_HEADER_LENGTH = 32;
const MAX_REGION_COLUMNS = 16;
const DEFAULT_REGION_DESCRIPTION = "New Region";

export class CustomFuelIndex extends AutogenLayoutCustomFuelIndex {
    private headerComponentsMap = new Map<number, { panel: Panel, textbox: Textbox, buttonEdit: Button, buttonSave?: Button, buttonCancel?: Button }>();
    private updateScheduled = false;
    private lastVisibleColumnIndex = 1;
    private didInitialQuickAddRebuild = false; // ensures we rebuild quick-add row once on initial open

    override onLoad(): void {
        this.layoutTableComment.displayDispatchField = false;
        this.layoutTableComment.displayCopyOrderFields = false;

        this.lastVisibleColumnIndex = 1; // reset on load
        // Initially hide all region columns beyond the first
        const table = this.tableCustomFscPrice;
        if (table) {
            for (let i = 2; i <= MAX_REGION_COLUMNS; i++) {
                table.setColumnVisibility(table.columns[i], false);
            }
        }

        // Ensure Add Column button is initially enabled (since we start with just 1 column)
        if (this.buttonAddColumn) {
            this.buttonAddColumn.enabled = true;
        }

        this.scheduleUpdateRegionColumns();
        this.sourceCustomFscIndex.addAfterExecutionListener(() => this.scheduleUpdateRegionColumns());
        this.sourceCustomFscIndex.addRemoteDataChangeListener(() => this.scheduleUpdateRegionColumns());
        this.sourceCustomFscIndex.doBeforeDataSourcePost = this.validateBeforePost;

        this.addUnmountListener(() => this.onUnloadInternal());
    }

    // Prevent posting if any region name is empty while its column has data
    // This is a last-chance check; UI should prevent this situation from arising
    async validateBeforePost() {
        let message: string = null;
        for (let i = 1; i <= this.lastVisibleColumnIndex; i++) {
            const regionName = this.sourceCustomFscIndex?.activeRow?.getString?.(`region_${i}_description`, null);
            if (regionName == null && this.columnHasAnyData(i)) {
                if (message === null) {
                    message = "Region name cannot be empty while column has data. Region(s):\n";
                }
                message += `- Column ${i}\n`;
            }
        }

        if (message !== null) {
            CommonDialogs.showError(message, "Error: Invalid Region Name(s)");
            return false;
        }

        return true;
    }

    tableCommentsOnRowDisplay(event: TableRowDisplayEvent) {
        const tableRow = event.target as TableRow;
        if (TableRowMode.QUICK_ADD === tableRow.mode) {
            const labelEnteredDate = tableRow.findComponentById("labelEnteredDate");
            if (labelEnteredDate) {
                labelEnteredDate.visible = false;
            }

            const textboxEnteredUserId = tableRow.findComponentById("textboxEnteredUserId") as Textbox;
            if (textboxEnteredUserId) {
                textboxEnteredUserId.visible = false;
            }
        }
    }

    tableCustomFscPriceOnRowDisplay(event: TableRowDisplayEvent) {
        this.updateRowPriceFields(event.target as TableRow);
    }

    textboxEnteredUserIdOnPrintable(event: PrintableEvent) {
        (event.target as Textbox).printable = true;
    }

    async buttonAddColumnOnClick() {
        // Safety checks - don't proceed if maximum columns already visible or button is disabled
        if (this.lastVisibleColumnIndex >= MAX_REGION_COLUMNS || (this.buttonAddColumn && !this.buttonAddColumn.enabled)) {
            return; // max columns reached
        }
        this.lastVisibleColumnIndex++;
        const table = this.tableCustomFscPrice;
        if (!table) {
            return;
        }
        const activeRow = this.sourceCustomFscIndex?.activeRow; if (!activeRow) return;
        // Set description on config (active) row so updateRegionColumns picks it up
        activeRow.set(`region_${this.lastVisibleColumnIndex}_description`, DEFAULT_REGION_DESCRIPTION);

        // Show column in existing data rows (components may already exist but hidden)
        table.rows?.forEach(row => {
            const priceTextbox = row.findComponentById(`textboxRegion${this.lastVisibleColumnIndex}Price`) as Textbox;
            if (priceTextbox) {
                priceTextbox.visible = true;
                priceTextbox.enabled = true;
            }
        });

        // Make the underlying column visible
        const col = table.columns.find(c => c?.headingCell?.id === `columnHeaderR${this.lastVisibleColumnIndex}`);
        if (col) {
            table.setColumnVisibility(col, true);
        }

        // Recompute headers / states
        this.updateRegionColumns();

        // Rebuild quick-add row so the new column cell is physically present, preserving any values previously typed
        await this.rebuildQuickAddRow();

        // Force a refresh to ensure displayData is invoked for new components
        this.forceTableRefresh();

        if (col?.headingCell?.element) {
            this.createOrUpdateHeaderButtons(this.lastVisibleColumnIndex, DEFAULT_REGION_DESCRIPTION);
            setTimeout(() => this.startHeaderEdit(this.lastVisibleColumnIndex), 50);
        }
    }

    // Rebuilds the quick-add row after structural column changes while keeping entered (in-memory) values
    private async rebuildQuickAddRow(): Promise<void> {
        const table = this.tableCustomFscPrice;
        if (!table) {
            return;
        }

        try {
            try {
                table.quickAddRow.updateDataFromComponents?.();
            } catch (err) {
                log.error("Error updating quickAddRow data from components:", err);
            }

            // Reset (destroys and recreates the quick-add row with current column visibility)
            await table.resetQuickAddRow();

        } catch (err) {
            log.error("Error rebuilding quickAddRow while preserving values:", err);
        }

        // Finally ensure visibility/enabled flags align
        this.updateRowPriceFields(table.quickAddRow);
        this.ensureQuickAddRowConsistency();
    }

    private scheduleUpdateRegionColumns() {
        if (this.updateScheduled) {
            return;
        }

        this.updateScheduled = true;
        requestAnimationFrame(() => {
            this.updateScheduled = false;
            this.updateRegionColumns();
        });
    }

    private hasDescription(regionNum: number): boolean {
        const activeRow = this.sourceCustomFscIndex?.activeRow;
        let rawVal = activeRow?.get?.(`region_${regionNum}_description`);
        if (rawVal == null || String(rawVal).trim() === "") {
            const tb = (this as any)[`textboxRegion${regionNum}`] as Textbox;
            rawVal = tb?.text ?? "";
        }
        return typeof rawVal === "string" && rawVal.trim() !== "";
    }

    private updateRegionColumns() {
        const activeRow = this.sourceCustomFscIndex?.activeRow;

        // loop through columns from the end to find the last one with a header description
        for (let i = MAX_REGION_COLUMNS; i >= 1; i--) {
            if (this.hasDescription(i)) {
                this.lastVisibleColumnIndex = i;
                break;
            }
        }

        // Update Add Column button state - disable when maximum columns are visible
        const addColumnButton = this.buttonAddColumn;
        if (addColumnButton) {
            addColumnButton.enabled = this.lastVisibleColumnIndex < MAX_REGION_COLUMNS;
        }

        const table = this.tableCustomFscPrice;
        if (!table) {
            return;
        }

        for (let i = 1; i <= this.lastVisibleColumnIndex; i++) {
            let rawVal = activeRow?.get?.(`region_${i}_description`) ?? "";
            if (rawVal == null || String(rawVal).trim() === "") {
                rawVal = (this as any)[`textboxRegion${i}`]?.text ?? "";
            }
            const hasDescription = this.hasDescription(i);
            const caption = hasDescription ? String(rawVal).trim() : "";
            const col = table.columns.find(c => c?.headingCell?.id === `columnHeaderR${i}`);

            if (!col) {
                continue;
            }

            table.setColumnVisibility(col, true);
            table.rows?.forEach(row => {
                const regionCell = row.findComponentById(`textboxRegion${i}Price`) as Textbox;
                if (regionCell) {
                    regionCell.visible = true;
                    regionCell.enabled = hasDescription;
                }
            });

            if (table.quickAddRow) {
                const quickAddCell = table.quickAddRow.findComponentById(`textboxRegion${i}Price`) as Textbox;
                if (quickAddCell) {
                    quickAddCell.visible = true;
                    quickAddCell.enabled = hasDescription;
                }
            }

            if (col.headingCell) {
                // Create or update header components (no need to check for element, components create their own DOM)
                this.createOrUpdateHeaderButtons(i, caption);
            }

            try {
                const cellComponent = col.cellDef?.def?.components?.[0];
                if (cellComponent && (cellComponent as any).enabled !== hasDescription) {
                    (cellComponent as any).enabled = hasDescription;
                }
            } catch (err) {
                log.error("Error updating cell component enabled state:", err);
            }

            const priceTextbox = (this as any)[`textboxRegion${i}Price`] as Textbox;
            if (priceTextbox && priceTextbox.enabled !== hasDescription) {
                priceTextbox.enabled = hasDescription;
            }
        }
        this.updateAllTableRows();

        // One-time rebuild after initial open so that all pre-existing visible columns appear in quick-add row
        if (!this.didInitialQuickAddRebuild && table.quickAddRow) {
            this.didInitialQuickAddRebuild = true;
            // Fire asynchronously to avoid recursive sync conflicts
            setTimeout(() => this.rebuildQuickAddRow(), 0);
        }
    }

    /**
     * Create or update the component-based header for a region column
     * @param regionNum The region number (1-MAX_REGION_COLUMNS)
     * @param caption Optional caption text (empty for new regions)
     */
    private createOrUpdateHeaderButtons(regionNum: number, caption: string = "") {
        const table = this.tableCustomFscPrice;
        if (!table) {
            return;
        }

        // Find the column
        const col = table.columns.find(c => c?.headingCell?.id === `columnHeaderR${regionNum}`);
        if (!col || !col.headingCell) {
            return;
        }

        const headingCell = col.headingCell;

        // Check if we already have components for this region
        let components = this.headerComponentsMap.get(regionNum);

        if (components) {
            // Update existing textbox value
            const displayVal = this.truncateCaption(caption || "");
            if (components.textbox.text !== displayVal) {
                components.textbox.text = displayVal;
            }

            // Verify the panel is actually in the heading cell
            // If it's not (e.g., heading cell was reset), re-add it
            if (!headingCell.components.includes(components.panel)) {
                log.info(`createOrUpdateHeaderPencil(${regionNum}): Panel not in headingCell, re-adding`);
                headingCell.caption = "";
                headingCell.field = ""; // Clear the field binding to prevent it from overwriting our components
                headingCell.removeAll();
                headingCell.add(components.panel);
            }

            return;
        }

        // Clear any existing content
        headingCell.caption = "";
        headingCell.field = ""; // Clear the field binding to prevent it from overwriting our components
        headingCell.align = HorizontalAlignment.LEFT;
        headingCell.removeAll();

        // Create new component structure
        const panel = new Panel();
        panel.wrap = true;
        panel.fillRow = true;

        // Create textbox for displaying/editing the region name
        const textbox = new Textbox();
        textbox.dataSource = this.mainDataSource;
        textbox.id = `textboxRegion${regionNum}`;
        textbox.field = `region_${regionNum}_description`;
        textbox.text = this.truncateCaption(caption || "");
        textbox.enabled = false; // Start in read-only mode
        textbox.rowBreak = true;
        textbox.clearButtonVisible = ClearButtonVisible.NO;

        // Create edit button
        const buttonEdit = new Button();
        buttonEdit.variant = ButtonVariant.round;
        buttonEdit.imageName = ImageName.pencil;
        buttonEdit.color = "primary";
        buttonEdit.tooltip = "Edit region name";
        buttonEdit.rowBreak = false;
        buttonEdit.visible = true; // Initially visible

        // Create save button (initially hidden)
        const buttonSave = new Button();
        buttonSave.variant = ButtonVariant.round;
        buttonSave.imageName = ImageName.disk;
        buttonSave.color = "primary";
        buttonSave.tooltip = "Save";
        buttonSave.rowBreak = false;
        buttonSave.visible = false; // Initially hidden

        // Create cancel button (initially hidden)
        const buttonCancel = new Button();
        buttonCancel.variant = ButtonVariant.round;
        buttonCancel.imageName = ImageName.cancel;
        buttonCancel.color = "primary";
        buttonCancel.tooltip = "Cancel";
        buttonCancel.rowBreak = false;
        buttonCancel.visible = false; // Initially hidden

        // Store component references (include save/cancel now)
        components = { panel, textbox, buttonEdit, buttonSave, buttonCancel };
        this.headerComponentsMap.set(regionNum, components);

        buttonEdit.addClickListener(() => {
            this.startHeaderEdit(regionNum);
        });

        buttonSave.addClickListener(() => {
            this.saveHeaderEdit(regionNum);
        });

        buttonCancel.addClickListener(() => {
            this.cancelHeaderEdit(regionNum);
        });

        // Setup textbox keydown listener for Tab key since blur doesn't always catch it
        textbox.addKeyDownListener((e) => {
            if (e.key === "Tab") {
                this.cancelHeaderEdit(regionNum);
            }
        });

        // Setup textbox blur listener
        textbox.addBlurListener((e) => {
            // Only auto-cancel if in edit mode (save/cancel buttons visible)
            if (!buttonSave.visible) {
                return; // Not in edit mode, ignore blur
            }

            const relatedTarget = (e as any).relatedTarget as HTMLElement;

            // If focus moved to save or cancel button, let the button's click handler take over
            if (relatedTarget && (
                relatedTarget === buttonSave.element ||
                relatedTarget === buttonCancel.element ||
                (buttonSave.element && buttonSave.element.contains(relatedTarget)) ||
                (buttonCancel.element && buttonCancel.element.contains(relatedTarget))
            )) {
                return; // Button click handler will handle it
            }

            // In all other cases (clicking elsewhere or tabbing away), cancel the changes
            setTimeout(() => {
                // Double-check we're still in edit mode
                if (buttonSave.visible) {
                    this.cancelHeaderEdit(regionNum);
                }
            }, 10);
        });

        // Add all components to panel
        panel.add(textbox);
        panel.add(buttonEdit);
        panel.add(buttonSave);
        panel.add(buttonCancel);

        // Add panel to heading cell
        headingCell.add(panel);

        log.info(`createOrUpdateHeaderPencil(${regionNum}): Added panel to headingCell. Panel has ${panel.components.length} children`);
    }

    // Guard: does any existing data row have a non-empty price value for this region?
    private columnHasAnyData(regionNum: number): boolean {
        const table = this.tableCustomFscPrice;
        if (!table) {
            return false;
        }

        let hasData = false;
        table.rows?.forEach(row => {
            if (hasData) {
                return;
            }

            const tb = row.findComponentById(`textboxRegion${regionNum}Price`) as Textbox;
            const val = tb?.text ?? "";
            if (String(val).trim() !== "") {
                hasData = true;
            }
        });
        return hasData;
    }

    private startHeaderEdit(regionNum: number) {
        const components = this.headerComponentsMap.get(regionNum);
        if (!components) {
            return;
        }

        const { textbox, buttonEdit, buttonSave, buttonCancel } = components;

        // Store original value for potential revert
        (textbox as any)._originalValue = textbox.text;

        // Style the textbox for editing mode
        textbox.enabled = true;
        textbox.focus();

        // Select all text in the textbox
        if (textbox.element) {
            const inputEl = textbox.element.querySelector("input");
            if (inputEl) {
                inputEl.select();
            }
        }

        // Toggle button visibility
        buttonEdit.visible = false;
        buttonSave.visible = true;
        buttonCancel.visible = true;
    }

    private saveHeaderEdit(regionNum: number) {
        const components = this.headerComponentsMap.get(regionNum);
        if (!components) {
            return;
        }

        const { textbox, buttonEdit, buttonSave, buttonCancel } = components;

        // Enforce length & trim whitespace
        const newValue = this.truncateCaption(textbox.text.trim());

        // Check if trying to clear a column that has data
        if (newValue === "" && this.columnHasAnyData(regionNum)) {
            // Revert to original value and show error
            textbox.text = (textbox as any)._originalValue || "";
            this.indicateBlockedEdit(textbox, "Cannot clear this region name while column has data.");
            textbox.focus();
            if (textbox.element) {
                const inputEl = textbox.element.querySelector("input");
                if (inputEl) {
                    inputEl.select();
                }
            }
            return; // Stay in edit mode
        }

        const originalValue = (textbox as any)._originalValue || "";

        // Exit edit mode
        textbox.enabled = false;
        buttonEdit.visible = true;
        buttonSave.visible = false;
        buttonCancel.visible = false;

        // Save changes if value changed
        if (newValue !== originalValue) {
            const activeRow = this.sourceCustomFscIndex?.activeRow;
            if (activeRow) {
                activeRow.set(`region_${regionNum}_description`, newValue);
                const configTextbox = (this as any)[`textboxRegion${regionNum}`] as Textbox;
                if (configTextbox && configTextbox.text !== newValue) {
                    configTextbox.text = newValue;
                }
                this.updateRegionColumns();
                this.updateAllTableRows();
            }
        }

        // Clean up stored original value
        delete (textbox as any)._originalValue;
    }

    private cancelHeaderEdit(regionNum: number) {
        const components = this.headerComponentsMap.get(regionNum);
        if (!components) {
            return;
        }

        const { textbox, buttonEdit, buttonSave, buttonCancel } = components;

        // Revert to original value
        const originalValue = (textbox as any)._originalValue || "";
        textbox.text = originalValue;
        this.mainDataSource.activeRow?.set(`region_${regionNum}_description`, originalValue);

        // Exit edit mode
        textbox.enabled = false;
        buttonEdit.visible = true;
        buttonSave.visible = false;
        buttonCancel.visible = false;

        // Clean up stored original value
        delete (textbox as any)._originalValue;
    }

    private indicateBlockedEdit(textbox: Textbox, message?: string) {
        if (message && textbox) {
            textbox.showAdhocTooltip(message);
        }
    }

    // Update enabled state of price fields in a specific row
    private updateRowPriceFields(row: TableRow) {
        if (!row) {
            return;
        }

        const activeRow = this.sourceCustomFscIndex?.activeRow;
        if (!activeRow) {
            return;
        }

        for (let i = 1; i <= this.lastVisibleColumnIndex; i++) {
            const tb = (this as any)[`textboxRegion${i}`] as Textbox;
            let rawVal = activeRow?.get?.(`region_${i}_description`);
            if (rawVal == null || String(rawVal).trim() === "") {
                rawVal = tb?.text ?? "";
            }

            const hasDescription = typeof rawVal === "string" && rawVal.trim() !== "";
            const priceTextbox = row.findComponentById(`textboxRegion${i}Price`) as Textbox;
            if (priceTextbox) {
                priceTextbox.visible = true;
                priceTextbox.enabled = hasDescription;
            }
        }

        for (let i = this.lastVisibleColumnIndex + 1; i <= MAX_REGION_COLUMNS; i++) {
            const priceTextbox = row.findComponentById(`textboxRegion${i}Price`) as Textbox;
            if (priceTextbox) {
                priceTextbox.visible = false;
                priceTextbox.enabled = false;
            }
        }
    }

    // Update enabled state of price fields in all visible table rows
    private updateAllTableRows() {
        const table = this.tableCustomFscPrice;
        if (!table) {
            return;
        }

        if (table.quickAddRow) {
            this.updateRowPriceFields(table.quickAddRow);
        }

        table.rows?.forEach(row => {
            if (row) {
                this.updateRowPriceFields(row);
            }
        });

        // Extra sync step to be safe
        this.ensureQuickAddRowConsistency();
    }

    // Ensure quick-add row matches current column visibility & enabled state; retry if not yet present
    private ensureQuickAddRowConsistency(attempt: number = 0) {
        const table = this.tableCustomFscPrice;
        if (!table) {
            return;
        }

        const qr = table.quickAddRow;
        if (!qr) {
            if (attempt < 5) { // retry a few animation frames until quickAddRow is available
                requestAnimationFrame(() => this.ensureQuickAddRowConsistency(attempt + 1));
            }
            return;
        }

        // Mirror per-column logic
        for (let i = 1; i <= MAX_REGION_COLUMNS; i++) {
            const priceTextbox = qr.findComponentById(`textboxRegion${i}Price`) as Textbox;
            if (!priceTextbox) {
                continue;
            }

            const visible = i <= this.lastVisibleColumnIndex; // only show within current range
            priceTextbox.visible = visible;
            if (visible) {
                // Enabled only if region description populated
                let rawVal = this.sourceCustomFscIndex?.activeRow?.get?.(`region_${i}_description`);
                if (rawVal == null || String(rawVal).trim() === "") {
                    rawVal = (this as any)[`textboxRegion${i}`]?.text ?? "";
                }
                const hasDescription = typeof rawVal === "string" && rawVal.trim() !== "";
                priceTextbox.enabled = hasDescription;
            } else {
                priceTextbox.enabled = false;
            }
        }
    }

    private forceTableRefresh() {
        const table = this.tableCustomFscPrice;
        if (!table) {
            return;
        }

        try {
            table.dataSource?.displayDataInBoundComponents?.();
        } catch (err) {
            log.error("Error displaying data in bound components:", err);
        }

        try {
            table.rows?.forEach(row => {
                for (let i = 1; i <= this.lastVisibleColumnIndex; i++) {
                    const tb = row.findComponentById(`textboxRegion${i}Price`) as any;
                    if (tb?.displayData) {
                        try {
                            tb.displayData(row["boundRow"] ?? null, null, 0);
                        } catch (err) {
                            log.error("Error displaying data in textboxRegion component:", err);
                        }
                    }
                }
            });

            if (table.quickAddRow) {
                for (let i = 1; i <= this.lastVisibleColumnIndex; i++) {
                    const tb = table.quickAddRow.findComponentById(`textboxRegion${i}Price`) as any;
                    if (tb?.displayData) {
                        try {
                            tb.displayData(table.quickAddRow["boundRow"] ?? null, null, 0);
                        } catch (err) {
                            log.error("Error displaying data in quickAddRow textboxRegion component:", err);
                        }
                    }
                }
            }
        } catch (err) {
            log.error("Error refreshing table rows:", err);
        }

        try {
            const orig: any = table.width;
            if (typeof orig === "number") {
                table.width = orig + 1;
                setTimeout(() => {
                    table.width = orig;
                }, 0);
            }
        } catch (err) {
            log.error("Error adjusting table width:", err);
        }
    }

    private onUnloadInternal(): void {
        // Clean up header components & listeners
        this.headerComponentsMap.forEach((components) => {
            if (components.panel && components.panel.element && components.panel.element.parentNode) {
                components.panel.element.parentNode.removeChild(components.panel.element);
            }
            components.buttonEdit.removeAllEventListeners("click");
            components.buttonSave.removeAllEventListeners("click");
            components.buttonCancel.removeAllEventListeners("click");
            components.textbox.removeAllEventListeners("click");
            components.textbox.removeAllEventListeners("keydown");
            components.textbox.removeAllEventListeners("blur");
        });
        this.headerComponentsMap.clear();
    }

    // Helper to consistently enforce the maximum header caption length
    private truncateCaption(value: string): string {
        if (!value) {
            return "";
        }

        if (value.length <= MAX_REGION_HEADER_LENGTH) {
            return value;
        }

        return value.substring(0, MAX_REGION_HEADER_LENGTH);
    }
}
